import React, { useEffect, useState } from "react";
import Popup from "../common/Popup";
import {
    DKLabel,
    DKButton,
    DKInput,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    DKIcon,
    DKIcons
} from "deskera-ui-library";
import BankAccountService from "../../services/BankAccountService";
import { useTranslation } from "react-i18next";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import { COUNTRY } from "../../constants/CountryConstant";
import Utility, { removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import { CONSTANTS } from "../../constants/Constant";

function AddBankAccount(props) {
    const { t } = useTranslation();

    const [isSG] = useState(TenantService.get(TENANT.COUNTRY_CODE) === COUNTRY.SG);
    const [isMY] = useState(TenantService.get(TENANT.COUNTRY_CODE) === COUNTRY.MY);

    const getInitialFormState = (country) => {
        switch (country) {
            case COUNTRY.SG:
                return {
                    bank: {
                        id: "bank",
                        title: t("BANK"),
                        type: INPUT_TYPE.DROPDOWN,
                        validation: [["required"]],
                        options: []
                    },
                    format: {
                        id: "format",
                        title: t("GIRO_FORMAT"),
                        type: INPUT_TYPE.DROPDOWN,
                        validation: [["required"]],
                        options: []
                    },
                    accountNumber: {
                        id: "accountNumber",
                        title: t("ACCOUNT_NUMBER"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"], ["pattern", "^[0-9]{9,10}$"]],
                        tooltip: `${t("ACCOUNT_NUMBER")} should have 9 to 10 digits.`
                    },
                    companyCode: {
                        id: "companyCode",
                        title: t("COMPANY_CODE"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"]],
                        hidden: true
                    },
                    branchCode: {
                        id: "branchCode",
                        title: t("BRANCH_CODE"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: `${t("BRANCH_CODE")} should have 3 digits.`
                    }
                };
            case COUNTRY.MY:
                return {
                    bank: {
                        id: "bank",
                        title: t("BANK"),
                        type: INPUT_TYPE.DROPDOWN,
                        validation: [["required"]],
                        options: []
                    },
                    accountHolderName: {
                        id: "accountHolderName",
                        title: t("ACCOUNT_HOLDER_NAME"),
                        type: INPUT_TYPE.TEXT,
                        validation: [["required"]]
                    },
                    accountNumber: {
                        id: "accountNumber",
                        title: t("ACCOUNT_NUMBER"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"], ["pattern", "^[0-9]{6,16}$"]],
                        tooltip: `${t("ACCOUNT_NUMBER")} should have 6 to 16 digits.`
                    },
                    companyCode: {
                        id: "companyCode",
                        title: t("ORGANIZATION_ID"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"]],
                        hidden: true
                    },
                    branchCode: {
                        id: "branchCode",
                        title: t("BRANCH_CODE"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: `${t("BRANCH_CODE")} should have 3 digits.`
                    },
                    bankCode: {
                        id: "bankCode",
                        title: t("BANK_CODE"),
                        type: INPUT_TYPE.NUMBER,
                        validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: `${t("BANK_CODE")} should have 3 digits.`
                    }
                };
            default:
                break;
        }
    };

    const [formState, setFormState] = useState(getInitialFormState(TenantService.get(TENANT.COUNTRY_CODE)));
    const [bankAccount, setBankAccount] = useState(-1);

    useEffect(() => {
        if (BankAccountService.BANK_ACCOUNT) {
            setBankAccount(BankAccountService.BANK_ACCOUNT);
            BankAccountService.BANK_ACCOUNT = null;
        } else {
            setBankAccount(null);
        }
    }, []);

    useEffect(() => {
        if (bankAccount !== -1) {
            getGIROBankDetails(bankAccount);
        }
    }, [bankAccount]);

    const getGIROBankDetails = (bankAccount) => {
        const timeout = showDelayedLoader();
        BankAccountService.getGIROBankDetails(TenantService.get(TENANT.COUNTRY_CODE)).then(
            (response) => {
                removeDelayedLoader(timeout);
                if (response?.banksDetails?.length > 0) {
                    const banks = [];
                    const formats = [];
                    response.banksDetails.forEach((bank) => {
                        banks.push({
                            value: bank.key,
                            label: bank.bank,
                            formats: bank.formats
                        });
                    });

                    const state = {...formState};
                    state.bank.options = banks;

                    if(bankAccount) {
                        setBankAccountValues(state, bankAccount);
                    }

                    setFormState(state);
                }
            }, (error) => {
                removeDelayedLoader(timeout);
            }
        );
    }

    const setBankAccountValues = (state, bankAccount) => {
        state.bank.value = bankAccount.bank;
        updateBankDependantFields(state, state.bank, state.bank.value);

        switch (TenantService.get(TENANT.COUNTRY_CODE)) {
            case COUNTRY.SG:
                state.format.value = bankAccount.format;
                break;
            case COUNTRY.MY:
                state.accountHolderName.value = bankAccount.accountHolderName;
                state.bankCode.value = bankAccount.bankCode;
                break;
            default:
                break;
        }
        
        updateBankAndFormatDependantFields(state);

        state.accountNumber.value = bankAccount.accountNumber;
        state.companyCode.value = bankAccount.companyCode;
        state.branchCode.value = bankAccount.branchCode;
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const getDropdownOption = (v, o) => {
        let option = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            option = o[index];
        }

        return option;
    }

    const getTooltipMessage = (field, min, max) => {
        if (min && max) {
            return `${field} should have ${min} to ${max} digits.`;
        } else {
            return `${field} should have ${min} digits.`;
        }
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "pattern":
                        if (!Utility.isEmpty(value) && !new RegExp(validation[1]).test(value)) {
                            error.invalid = true;
                            error.errorMessage = "Invalid " + field.title;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const getValidations = (state) => {
        let bank;
        let format;
        let validation;

        switch (TenantService.get(TENANT.COUNTRY_CODE)) {
            case COUNTRY.SG:
                bank = state.bank.value;
                format = state.format.value;
                validation = {
                    accountNumber: {
                        validation: [["required"], ["pattern", "^[0-9]{9,10}$"]],
                        tooltip: getTooltipMessage(state.accountNumber.title, 9, 10)
                    },
                    companyCode: {
                        validation: [],
                        tooltip: null
                    },
                    branchCode: {
                        validation:[["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: getTooltipMessage(state.branchCode.title, 3)
                    }
                };

                switch (bank) {
                    case "OCBC":
                        switch (format) {
                            case "OCBC GIRO":
                                validation.accountNumber = {
                                    validation: [["required"], ["pattern", "^[0-9]{9}$"]],
                                    tooltip: getTooltipMessage(state.accountNumber.title, 9)
                                };
                                break;
                            case "OCBC Velocity GIRO":
                                validation.accountNumber = {
                                    validation: [["required"], ["pattern", "^[0-9]{1,34}$"]],
                                    tooltip: getTooltipMessage(state.accountNumber.title, 1, 34)
                                };
                                validation.branchCode = {
                                    validation: [["required"], ["pattern", "^[0-9]{1,11}$"]],
                                    tooltip: getTooltipMessage(state.branchCode.title, 1, 11)
                                };
                                break;
                            default:
                                break;
                        }
                        break;
                    case "DBS":
                        validation.companyCode = {
                            validation: [["required"]],
                            tooltip: null
                        };
                        break;
                    default:
                        break;
                }
                break;
            case COUNTRY.MY:
                bank = state.bank.value;
                validation = {
                    accountNumber: {
                        validation: [["required"], ["pattern", "^[0-9]{6,16}$"]],
                        tooltip: getTooltipMessage(state.accountNumber.title, 6, 16)
                    },
                    companyCode: {
                        validation: [],
                        tooltip: null
                    },
                    branchCode: {
                        validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: getTooltipMessage(state.branchCode.title, 3)
                    },
                    bankCode: {
                        validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                        tooltip: getTooltipMessage(state.branchCode.title, 3)
                    }
                };

                switch (bank) {
                    case "MayBank":
                    case "MayBank(2E)":
                        validation.branchCode = {
                            validation: [["required"], ["pattern", "^[0-9]{5}$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 5)
                        };
                        validation.companyCode = {
                            validation: [["required"], ["pattern", "^[0-9]{5}$"]],
                            tooltip: getTooltipMessage(state.companyCode.title, 5)
                        };
                        validation.bankCode = {
                            validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 3)
                        }
                        break;
                    case "UOB Bank":
                        validation.accountNumber = {
                            validation: [["required"], ["pattern", "^[0-9]{9,17}$"]],
                            tooltip: getTooltipMessage(state.accountNumber.title, 9, 17)
                        };
                        validation.bankCode = {
                            validation: [["required"], ["pattern", "^[0-9]{4}$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 4)
                        }
                        break;
                    case "CIMB Bank":
                        validation.branchCode = {
                            validation: [["required"], ["pattern", "^([XXXxxx]{3}|[XXXxxx]{4}|[0-9]{3,4})$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 3, 4)
                        };
                        validation.companyCode = {
                            validation: [["required"], ["pattern", "^[0-9]{5}$"]],
                            tooltip: getTooltipMessage(state.companyCode.title, 5)
                        };
                        validation.bankCode = {
                            validation: [["required"], ["pattern", "^[0-9]{9}$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 9)
                        }
                        break;
                    case "Public Bank Berhad":
                        validation.bankCode = {
                            validation: [["required"], ["pattern", "^[0-9]{3}$"]],
                            tooltip: getTooltipMessage(state.branchCode.title, 3)
                        }
                        break;
                    default:
                        validation.bankCode = {
                            validation: [],
                            tooltip: CONSTANTS.EMPTY_STRING
                        }
                        break;
                }
                break;
            default:
                validation = {
                    accountNumber: {
                        validation: [["required"]],
                        tooltip: null
                    },
                    companyCode: {
                        validation: [],
                        tooltip: null
                    },
                    branchCode: {
                        validation:[["required"]],
                        tooltip: null
                    }
                }
                break;
        }

        return validation
    }

    const getGIROFormatOptions = (bank, value) => {
        const bankOption = getDropdownOption(value, bank.options);
        if (bankOption?.formats) {
            return bankOption.formats.map((format) => {
                return {
                    value: format,
                    label: format
                }
            });
        }

        return [];
    }

    const updateBankDependantFields = (state, field, value) => {
        if (isSG) {
            state.format.value = null;
            state.format.options = getGIROFormatOptions(field, value);
        }

        state.companyCode.hidden = true;

        switch (TenantService.get(TENANT.COUNTRY_CODE)) {
            case COUNTRY.SG:
                if (value === "DBS") {
                    state.companyCode.hidden = false;
                }
                break;
            case COUNTRY.MY:
                state.bankCode.hidden = true;
                state.branchCode.value = CONSTANTS.EMPTY_STRING;
                switch(value) {
                    case "MayBank":
                    case "MayBank(2E)":
                    case "CIMB Bank":
                        state.bankCode.hidden = false;
                        state.companyCode.hidden = false;
                        state.companyCode.title = (value === "MayBank(2E)") ? t("ORGANIZATION_ID") : (value === "MayBank" ? t("ORIGINATOR_ID") :t("ORGANIZATION_CODE"));
                        break;
                    case "RHB Bank":
                    case "AmBank":
                        state.bankCode.hidden = true;
                        break;
                    case "Public Bank Berhad":
                        state.bankCode.hidden = false;
                        break;
                    case "UOB Bank":
                        state.bankCode.hidden = false;
                        state.branchCode.value = "000";
                        break;
                }
                break;
            default:
                break;
        }
    }

    const updateBankAndFormatDependantFields = (state) => {
        const validators = getValidations(state);

        state.accountNumber = {...state.accountNumber, ...validators.accountNumber};
        state.companyCode = {...state.companyCode, ...validators.companyCode};
        state.branchCode = {...state.branchCode, ...validators.branchCode};
        state.bankCode = {...state.bankCode, ...validators.bankCode};
    }

    const updateState = (stateName, field, value) => {
        const state = {...formState};

        state[stateName] = {...field, value: value, ...hasError(field, value)};

        if (stateName === "bank") {
            updateBankDependantFields(state, field, value);
        }

        if (stateName === "bank" || stateName === "format") {
            updateBankAndFormatDependantFields(state);
        }

        setFormState(state);
    }

    const isInvalid = () => {
        let invalid = false;

        const state = {...formState};
        Object.entries(state).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(state);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            accountType: "GIRO",
            key: getDropdownValue(formState.bank.value, formState.bank.options),
            bank: getDropdownLabel(formState.bank.value, formState.bank.options),
            accountNumber: formState.accountNumber.value,
            companyCode: formState.companyCode.value,
            branchCode: formState.branchCode.value
        }

        switch (TenantService.get(TENANT.COUNTRY_CODE)) {
            case COUNTRY.SG:
                payload.format = getDropdownLabel(formState.format.value, formState.format.options);
                payload.bankCode = CONSTANTS.EMPTY_STRING;
                break;
            case COUNTRY.MY:
                payload.accountHolderName = formState.accountHolderName.value;
                payload.bankCode = formState.bankCode.value;
                break;
            default:
                break;
        }

        return payload;
    }

    const saveBankAccount = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();
        if (bankAccount) {
            showLoader(t("UPDATING_BANK_ACCOUNT"));
            BankAccountService.updateBankAccount(bankAccount.id, payload).then(
                (response) => {
                    removeLoader();
                    showToast(t("BANK_UPDATE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    props.onClose(true);
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader(t("SAVING_BANK_ACCOUNT"));
            BankAccountService.saveBankAccount(payload).then(
                (response) => {
                    showToast(t("BANK_SAVE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    removeLoader();
                    props.onClose(true);
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        }
    }

    const getTooltip = (obj) => {
        return (
            <div class="custom-tooltip position-relative width-auto ">
                <DKIcon
                    src={DKIcons.ic_info}
                    className="ml-s ic-s ml-r  cursor-hand "
                    style={{ opacity: 0.4 }}
                />
                <span class={`custom-tooltiptext dk-input-holder border-radius-m 
                                    shadow-m bg-white text-dark-gray border-m 
                                    position-fixed p-m border-box z-index-6 ${obj.tooltipClass}`}>
                    <DKLabel text={obj.tooltip}/>
                </span>
            </div>
        )
    }

    const renderField = (stateName, field, width) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="pb-l mb-s position-relative">
                            <div className="row">
                                <DKLabel className="" text={`${field.title} ${isRequired(field.validation) ? "*" : ""}`} />
                                    {field.tooltip && field.tooltip != "" && (
                                        getTooltip(field)
                                )}
                            </div>
                            
                            <DKInput
                                title={""}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={(value) => {
                                    return !field.invalid;
                                }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                dropdownConfig={{
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    },
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={() => { }}
                                validator={(value) => {
                                    return !field.invalid;
                                }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <Popup>
            <div className="parent-width">
                <div className="row">
                    <DKLabel text={bankAccount ? t("EDIT_BANK_ACCOUNT") : t("ADD_NEW_BANK_ACCOUNT")} className="fs-l fw-b" />
                    <div className="ml-auto d-flex">
                        <DKButton
                            title={t("CANCEL")}
                            className="border-radius-m bg-gray1 border-m"
                            onClick={() => {
                                props.onClose();
                            }}
                        />
                        <DKButton
                            title={t("SAVE")}
                            className="bg-app text-white ml-m"
                            onClick={() => {
                                saveBankAccount();
                            }}
                        />
                    </div>
                </div>
                <div className="mt-m">
                    {renderField("bank", formState.bank, "100%")}
                    {isSG && renderField("format", formState.format, "100%")}
                    {isMY && renderField("accountHolderName", formState.accountHolderName, "100%")}
                    {renderField("accountNumber", formState.accountNumber, "100%")}
                    {!formState.companyCode.hidden && renderField("companyCode", formState.companyCode, "100%")}
                    {renderField("branchCode", formState.branchCode, "100%")}
                    {isMY && !formState.bankCode.hidden && renderField("bankCode", formState.bankCode, "100%")}
                </div>
            </div>
        </Popup>
    );
}

export default AddBankAccount;
